import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DEFAULT_PAGE_TAKE } from 'shared/constants/filters'
import {
  GET_HISTORIC_APPROVALS,
  GET_INVOICE_APPROVALS,
  GET_INVOICE_ARCHIVE,
} from 'shared/constants/storeTags'
import { emptySplitApi } from 'shared/store/store'
import {
  IInvoiceFilters,
  InvoiceScopeEnum,
  InvoiceSortFieldEnum,
  InvoiceSortOrderEnum,
} from '../models/Invoice'

interface State {
  filters: {
    approvalFilters: IInvoiceFilters
    historicApprovalsFilters: IInvoiceFilters
    archiveFilters: IInvoiceFilters
  }
  selected: string[]
}

const defaultFilters = {
  searchText: '',
  sortorder: InvoiceSortOrderEnum.desc,
  clientId: null,
  take: DEFAULT_PAGE_TAKE,
  skip: 0,
}

const initialState: State = {
  filters: {
    approvalFilters: {
      ...defaultFilters,
      scope: InvoiceScopeEnum.mine,
      status: {
        forProcessing: true,
        rejectedOrOnHold: true,
        completed: false,
      },
      sort: InvoiceSortFieldEnum.deadline,
    },
    historicApprovalsFilters: {
      ...defaultFilters,
      scope: InvoiceScopeEnum.mine,
      sort: InvoiceSortFieldEnum.invoiceDate,
    },
    archiveFilters: {
      ...defaultFilters,
      scope: InvoiceScopeEnum.notPaid,
      sort: InvoiceSortFieldEnum.invoiceDate,
    },
  },
  selected: [],
}

type FilterAction = {
  filters: Partial<IInvoiceFilters>
  type: 'approvalFilters' | 'historicApprovalsFilters' | 'archiveFilters'
}

const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    // Not exported as it is used internally only
    setFilters: (state, action: PayloadAction<FilterAction>) => {
      state.filters[action.payload.type] = {
        ...state.filters[action.payload.type],
        ...action.payload.filters,
      }
    },
    addSelectedInvoice: (state, action: PayloadAction<string>) => {
      state.selected = [...state.selected, action.payload]
    },
    removeSelectedInvoice: (state, action: PayloadAction<string>) => {
      state.selected = state.selected.filter((id) => id !== action.payload)
    },
    resetSelectedInvoices: (state) => {
      state.selected = []
    },
  },
  selectors: {
    getInvoiceFilters: (state: State) => state.filters,
    getSelectedInvoices: (state: State) => state.selected,
  },
})

export const setInvoiceFilters = createAsyncThunk(
  'invoice/setFilters',
  async ({ filters, type }: FilterAction, thunkApi) => {
    await thunkApi.dispatch(invoiceSlice.actions.setFilters({ filters, type }))
    const tag: Record<FilterAction['type'], string> = {
      approvalFilters: GET_INVOICE_APPROVALS,
      historicApprovalsFilters: GET_HISTORIC_APPROVALS,
      archiveFilters: GET_INVOICE_ARCHIVE,
    }
    thunkApi.dispatch(emptySplitApi.util.invalidateTags([tag[type]]))
  }
)

export const {
  addSelectedInvoice,
  removeSelectedInvoice,
  resetSelectedInvoices,
} = invoiceSlice.actions
export const { getInvoiceFilters, getSelectedInvoices } = invoiceSlice.selectors
export default invoiceSlice
